import {
	useDeleteVetterEntityMutation,
	useGetAllVetterEntitiesQuery,
	useGetVetterStatsQuery,
} from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import { toast } from 'sonner';
import { EditVetterModal, type VetterData } from './EditVetterModal';
import { InviteVetterModal } from './InviteVetterModal';
import { Mail, MoreHorizontal, Pencil, Phone, Plus, Shield, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { SearchInput } from '../../components/ui/search-input';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { Tabs } from '../../components/ui/tabs';
import { StatusBadge } from '../../components/ui/status-badge';
import { InitialsAvatar } from '../../components/ui/initials-avatar';
import { ViewToggle } from '../../components/ui/view-toggle';
import { EntityCard } from '../../components/ui/entity-card';
import { formatDate, formatMonthYear } from '../../lib/utils';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam } from '../../hooks/useQueryParam';
import { CardSkeletons, TableSkeleton } from '../../components/ui/list-skeletons';
import { EmptyState } from '../../components/ui/empty-state';
import { SortableTableHead } from '../../components/ui/sortable-table-head';

/**
 * One row of vetters as laid out at the current viewport, for sizing scroll-mode batches.
 * Mirrors the card grid below (1/2/3 columns at sm/lg, 14px gap) of EntityCards: the ~142px
 * shell (padding, 44px avatar header, metrics strip) plus the ~95px primary-contact block.
 * A table row is the two-line primary contact cell plus cell padding and border.
 */
function vetterRows(view: string): RowEstimate {
	if (view !== 'card') return { rowHeight: 57 };
	const vw = window.innerWidth;
	const columns = vw >= 1024 ? 3 : vw >= 640 ? 2 : 1;
	return { columns, rowHeight: 142 + 95 + 14 };
}

/* ════════════════════════ VETTERS PAGE ════════════════════════ */

export function VettersPage() {
	const navigate = useNavigate();
	useSetPageMeta('Vetters', [{ label: 'Vetters' }]);
	const {
		search,
		setSearch,
		debouncedSearch,
		setPage,
		view,
		setView,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		limit,
		mode,
		setMode,
		request,
		loadMore,
		reload,
	} = useListParams({
		defaultSort: 'name',
		defaultDir: 'asc',
		defaultView: 'card',
		scrollRows: vetterRows,
	});
	const [tab, setTab] = useQueryParam('tab', 'all');
	const [inviteOpen, setInviteOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<{
		id: string;
		name: string;
		memberCount: number;
	} | null>(null);
	const [editTarget, setEditTarget] = useState<VetterData | null>(null);

	const isActiveFilter = tab === 'active' ? true : tab === 'inactive' ? false : undefined;

	const { data, loading, refetch } = useGetAllVetterEntitiesQuery({
		variables: {
			input: {
				pagination: request,
				order: { field: sortField, direction: sortDirection },
				search: debouncedSearch || undefined,
				isActive: isActiveFilter,
			},
		},
		fetchPolicy: 'no-cache',
	});

	const list = usePagedList(
		{ mode, request, limit, setPage, loadMore },
		data?.getAllVetterEntity,
		loading
	);
	const vetters = list.items;

	// Dataset-wide KPI stats (independent of the active tab/search/page).
	const { data: statsData, refetch: refetchStats } = useGetVetterStatsQuery({
		fetchPolicy: 'no-cache',
	});
	const stats = statsData?.getVetterStats;

	const refresh = () => {
		reload(refetch);
		refetchStats();
	};

	const [deleteVetterEntity, { loading: deleting }] = useDeleteVetterEntityMutation();
	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deleteVetterEntity({ variables: { id: deleteTarget.id } });
			toast.success('Vetter deleted');
			setDeleteTarget(null);
			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete vetter');
		}
	};

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Header ── */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
						Vetters
					</h1>
					<p className="text-base text-text-3">
						Independent quality auditors. Book on-site visits to verify production runs.
					</p>
				</div>
				<Button variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
					<Plus size={14} data-icon="inline-start" />
					Add vetter
				</Button>
			</div>

			{/* ── KPI strip ── */}
			<div className="mb-7 grid grid-cols-2 divide-x divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken lg:grid-cols-4">
				{[
					{ label: 'Total', value: stats?.total ?? 0, sub: 'All vetters' },
					{ label: 'Active', value: stats?.active ?? 0, sub: 'All vetters' },
					{ label: 'Inactive', value: stats?.inactive ?? 0, sub: 'All vetters' },
					{
						label: 'Team members',
						value: stats?.teamMembers ?? 0,
						sub: 'Across all vetters',
					},
				].map((k) => (
					<div key={k.label} className="px-5 py-4">
						<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
							{k.label}
						</div>
						<div className="mb-1 font-mono text-2xl font-medium text-text-1">
							{k.value}
						</div>
						<div className="text-xs text-text-3">{k.sub}</div>
					</div>
				))}
			</div>

			{/* ── Tabs + filters row ── */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						setTab(v);
						setPage(1);
					}}
					items={[
						{ value: 'all', label: 'All vetters' },
						{ value: 'active', label: 'Active' },
						{ value: 'inactive', label: 'Inactive' },
					]}
				/>
				<div className="flex items-center gap-2">
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Search vetters…"
						className="flex-1 lg:flex-none lg:w-[200px]"
					/>
					<ViewToggle value={view} onChange={setView} />
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{/* ── Content: Card view or List view ── */}
			{list.loading ? (
				view === 'card' ? (
					<CardSkeletons />
				) : (
					<TableSkeleton />
				)
			) : vetters.length === 0 ? (
				<EmptyState
					icon={Shield}
					title="No vetters found"
					description={
						search
							? 'Try a different search term.'
							: tab === 'active'
								? 'No active vetters.'
								: tab === 'inactive'
									? 'No inactive vetters.'
									: 'No vetter entities have been created yet.'
					}
				/>
			) : view === 'card' ? (
				/* ── Card grid (3 columns) ── */
				<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
					{vetters.map((v) => (
						<EntityCard
							key={v.id}
							name={v.name}
							subtitle={v.description || 'Vetter entity'}
							badge={<StatusBadge active={v.isActive} />}
							actions={
								<DropdownMenu>
									<DropdownMenuTrigger
										onClick={(e) => e.stopPropagation()}
										className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
									>
										<MoreHorizontal size={14} />
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem
											onClick={(e) => {
												e.stopPropagation();
												setEditTarget({
													id: v.id,
													name: v.name,
													description: v.description,
													notes: v.notes,
													isActive: v.isActive,
													primaryContactVetterUserId:
														v.primaryContactVetterUserId,
													members: v.vetterUsers ?? [],
												});
											}}
										>
											<Pencil size={14} /> Edit
										</DropdownMenuItem>
										<DropdownMenuItem
											variant="destructive"
											onClick={(e) => {
												e.stopPropagation();
												setDeleteTarget({
													id: v.id,
													name: v.name,
													memberCount: (v.vetterUsers ?? []).length,
												});
											}}
										>
											<Trash2 size={14} /> Delete
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							}
							metrics={[
								{ label: 'Members', value: (v.vetterUsers ?? []).length },
								{
									label: 'Created',
									value: v.createdAt ? formatMonthYear(v.createdAt) : '—',
								},
							]}
							metricColumns={2}
							onClick={() => navigate(`/vetters/${v.id}`)}
						>
							<div className="mb-3.5">
								<div className="mb-1 text-2xs font-medium uppercase tracking-[0.06em] text-text-4">
									Primary contact
								</div>
								{v.primaryContact ? (
									<div className="flex flex-col gap-1">
										<div className="flex items-center gap-1.5 text-sm text-text-2">
											<Star size={14} className="shrink-0 text-text-4" />
											<span className="truncate">
												{v.primaryContact.firstName}{' '}
												{v.primaryContact.lastName}
											</span>
										</div>
										{/* Both lines always render — a missing one reads as
										    "not on file" rather than silently collapsing. */}
										<div className="flex items-center gap-1.5 text-sm text-text-2">
											<Mail size={14} className="shrink-0 text-text-4" />
											<span className="truncate">
												{v.primaryContact.authenticationUser?.email || (
													<span className="text-text-4">—</span>
												)}
											</span>
										</div>
										<div className="flex items-center gap-1.5 text-sm text-text-2">
											<Phone size={14} className="shrink-0 text-text-4" />
											<span className="truncate">
												{v.primaryContact.phone || (
													<span className="text-text-4">—</span>
												)}
											</span>
										</div>
									</div>
								) : (
									<div className="text-sm text-text-4">Not set</div>
								)}
							</div>
						</EntityCard>
					))}
				</div>
			) : (
				/* ── List/table view ── */
				<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[850px]">
							<TableHeader>
								<TableRow className="border-line-1 hover:bg-transparent">
									<SortableTableHead
										sortKey="name"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[24%] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Vetter
									</SortableTableHead>
									<TableHead className="w-[22%] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Primary contact
									</TableHead>
									<SortableTableHead
										sortKey="description"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[22%] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Description
									</SortableTableHead>
									<TableHead className="w-[16%] text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Team
									</TableHead>
									<TableHead className="w-15 text-center text-xs font-medium uppercase tracking-[0.06em] text-text-4">
										Users
									</TableHead>
									<SortableTableHead
										sortKey="isActive"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[90px] text-center text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Status
									</SortableTableHead>
									<SortableTableHead
										sortKey="createdAt"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Created
									</SortableTableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{vetters.map((v) => (
									<TableRow
										key={v.id}
										className="cursor-pointer border-line-1 transition-colors duration-[180ms] hover:bg-surface-hover last:border-b-0"
										onClick={() => navigate(`/vetters/${v.id}`)}
									>
										<TableCell>
											<div className="flex items-center gap-3">
												<InitialsAvatar
													name={v.name}
													size={32}
													shape="rounded"
												/>
												<span className="text-base font-medium text-text-1">
													{v.name}
												</span>
											</div>
										</TableCell>
										<TableCell>
											{v.primaryContact ? (
												<div className="min-w-0">
													<div className="truncate text-base text-text-2">
														{v.primaryContact.firstName}{' '}
														{v.primaryContact.lastName}
													</div>
													{v.primaryContact.authenticationUser?.email && (
														<div className="truncate text-xs text-text-4">
															{
																v.primaryContact.authenticationUser
																	.email
															}
														</div>
													)}
												</div>
											) : (
												<span className="text-base text-text-3">—</span>
											)}
										</TableCell>
										<TableCell className="max-w-[240px] truncate text-base text-text-2">
											{v.description || '—'}
										</TableCell>
										<TableCell className="text-base text-text-2">
											{v.vetterUsers
												?.map((u) => `${u.firstName} ${u.lastName}`)
												.join(', ') || '—'}
										</TableCell>
										<TableCell className="text-center font-mono text-base text-text-2">
											{v.vetterUsers?.length ?? 0}
										</TableCell>
										<TableCell className="text-center">
											<StatusBadge active={v.isActive} />
										</TableCell>
										<TableCell className="text-base text-text-3">
											{v.createdAt ? formatDate(v.createdAt) : '—'}
										</TableCell>
										<TableCell>
											<div className="flex justify-end">
												<DropdownMenu>
													<DropdownMenuTrigger
														onClick={(e) => e.stopPropagation()}
														className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
													>
														<MoreHorizontal size={14} />
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															onClick={(e) => {
																e.stopPropagation();
																setEditTarget({
																	id: v.id,
																	name: v.name,
																	description: v.description,
																	notes: v.notes,
																	isActive: v.isActive,
																	primaryContactVetterUserId:
																		v.primaryContactVetterUserId,
																	members: v.vetterUsers ?? [],
																});
															}}
														>
															<Pencil size={14} /> Edit
														</DropdownMenuItem>
														<DropdownMenuItem
															variant="destructive"
															onClick={(e) => {
																e.stopPropagation();
																setDeleteTarget({
																	id: v.id,
																	name: v.name,
																	memberCount: (
																		v.vetterUsers ?? []
																	).length,
																});
															}}
														>
															<Trash2 size={14} /> Delete
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</Card>
			)}

			<ListFooter list={list} />

			<InviteVetterModal
				open={inviteOpen}
				onOpenChange={setInviteOpen}
				onSuccess={() => refresh()}
			/>

			<EditVetterModal
				open={!!editTarget}
				onOpenChange={(open) => {
					if (!open) setEditTarget(null);
				}}
				vetter={editTarget}
				onSuccess={() => refresh()}
			/>

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
				title="Delete vetter"
				description={`Are you sure you want to delete "${deleteTarget?.name}"?${
					deleteTarget?.memberCount
						? ` This will also remove its ${deleteTarget.memberCount} team member${
								deleteTarget.memberCount === 1 ? '' : 's'
							}.`
						: ''
				} This action cannot be undone.`}
				onConfirm={handleDelete}
				loading={deleting}
			/>
		</div>
	);
}
